import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store/root-reducer';

import { capProfileCount } from '../domain/export-plan.model';

const selectProfileExport = (state: RootState) => state.profileExport;

// ── Route context ──
export const selectListId = (state: RootState) => selectProfileExport(state).listId;
export const selectDeactivated = (state: RootState) => selectProfileExport(state).deactivated;
export const selectLicenceId = (state: RootState) => selectProfileExport(state).licenceId;
export const selectFilters = (state: RootState) => selectProfileExport(state).filters;
export const selectProfileCount = (state: RootState) => selectProfileExport(state).profileCount;
export const selectBaseName = (state: RootState) => selectProfileExport(state).baseName;

/** Count shown to the user; the backend exports at most 5 000 rows. */
export const selectDisplayCount = createSelector(selectProfileCount, capProfileCount);

// ── Wizard ──
export const selectStep = (state: RootState) => selectProfileExport(state).step;
export const selectDone = (state: RootState) => selectProfileExport(state).done;
export const selectFormat = (state: RootState) => selectProfileExport(state).format;
export const selectDataTypes = (state: RootState) => selectProfileExport(state).dataTypes;
export const selectWantProfiles = createSelector(selectDataTypes, (types) =>
  types.includes('profiles'),
);
export const selectWantCompanies = createSelector(selectDataTypes, (types) =>
  types.includes('companies'),
);
export const selectProfileColumns = (state: RootState) => selectProfileExport(state).profileColumns;
export const selectCompanyColumns = (state: RootState) => selectProfileExport(state).companyColumns;

/** Columns effectively exported (only the sections whose data type is active). */
export const selectTotalColumnCount = createSelector(
  selectWantProfiles,
  selectWantCompanies,
  selectProfileColumns,
  selectCompanyColumns,
  (wantProfiles, wantCompanies, profileColumns, companyColumns) =>
    (wantProfiles ? profileColumns.length : 0) + (wantCompanies ? companyColumns.length : 0),
);

export const selectExporting = (state: RootState) => selectProfileExport(state).exporting;
export const selectError = (state: RootState) => selectProfileExport(state).error;

/** The export button needs every active data type to have at least one column. */
export const selectExportDisabled = createSelector(
  selectExporting,
  selectWantProfiles,
  selectWantCompanies,
  selectProfileColumns,
  selectCompanyColumns,
  (exporting, wantProfiles, wantCompanies, profileColumns, companyColumns) =>
    exporting ||
    (wantProfiles ? profileColumns.length : 0) + (wantCompanies ? companyColumns.length : 0) === 0 ||
    (wantProfiles && profileColumns.length === 0) ||
    (wantCompanies && companyColumns.length === 0),
);
